import { Routes ,Route, Navigate, useParams } from "react-router-dom";
import withTenant from './utils/withTenant';
import { Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ThemeProvider } from "./context/ThemeContext";

import NotFound from "./NotFound";
import TokenRedirect from "./pages/TokenRedirect";
import Logout from "./pages/Logout";
import HeaderNavBar from "./components/layout/HeaderNavBar";
import Footer from "./components/layout/Footer";
import MenuSide from "./components/layout/MenuSide";
import withGuard from "./utils/withGuard";
import { useTenant,useStartup } from "./hooks";
import { MwModal, MwSpinner } from "./components/ui";
import NoPayment from "./components/suspend/NoPayment";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { fetchUserPermissions, getAppPermission, getCurrentAppId } from "./utils/permissionsFilter";
import lazyPage from './utils/lazyPage';
import MwLoadingOverlay from './components/ui/MwLoadingOverlay';

// Pages are downloaded when first opened instead of all with the first visit (the app used to ship
// every page, with Excel/PDF/charts/editor libraries, in one file).
const ManageInvoices = lazyPage(() => import(/* webpackPrefetch: true */ './pages/invoices/ManageInvoices'));
const CreateInvoice = lazyPage(() => import(/* webpackPrefetch: true */ './pages/invoices/CreateInvoice'));
const EditInvoice = lazyPage(() => import('./pages/invoices/EditInvoice'));
const EditQuote = lazyPage(() => import('./pages/quotes/EditQuote'));
const CreateQuote = lazyPage(() => import(/* webpackPrefetch: true */ './pages/quotes/CreateQuote'));
const ManageQuotes = lazyPage(() => import(/* webpackPrefetch: true */ './pages/quotes/ManageQuotes'));
const DashBoard = lazyPage(() => import(/* webpackPrefetch: true */ './pages/DashBoard'));
const ShowInvoice = lazyPage(() => import(/* webpackPrefetch: true */ './components/invoice/ShowInvoice'));
const ShowQuote = lazyPage(() => import(/* webpackPrefetch: true */ './components/quote/ShowInvoice'));
const Settiings = lazyPage(() => import('./pages/Settiings'));
const Customers = lazyPage(() => import('./pages/Customers'));
const CreateCustomer = lazyPage(() => import('./components/Customers/CreateCustomer'));
const ShowCustomer = lazyPage(() => import('./components/Customers/ShowCustomer'));
const Salesmans = lazyPage(() => import('./pages/Salesmans'));
const CreateSalesman = lazyPage(() => import('./components/Salesmans/CreateSalesman'));
const Products = lazyPage(() => import('./pages/products/Products'));
const CreateProduct = lazyPage(() => import('./components/Products/CreateProduct'));
const ProductsHome = lazyPage(() => import('./pages/products/ProductsHome'));
const EditSalesman = lazyPage(() => import('./components/Salesmans/EditSalesman'));
const Reports = lazyPage(() => import('./pages/Reports'));
const CreateReturnsInvoice = lazyPage(() => import('./pages/invoices-returns/CreateReturnsInvoice'));
const ShowInvoiceReturns = lazyPage(() => import('./components/invoice-returns/ShowInvoiceReturns'));
const Transactions = lazyPage(() => import('./pages/transactions/Transactions'));
const QuotePdf = lazyPage(() => import('./components/quote/QuotePdf'));
const OsViewWeights = lazyPage(() => import('./components/OsNewWeight/OsViewWeights'));
const OsCreateWeight = lazyPage(() => import('./components/OsNewWeight/OsCreateWeight'));
const OsEditWeight = lazyPage(() => import('./components/OsNewWeight/OsEditWeight'));
const OsUnitDetails = lazyPage(() => import('./components/OsNewWeight/OsUnitDetails'));
const CustomTable = lazyPage(() => import('./components/OsLIstsOfPrices/OsCustomTable'));
const OsCreateInvoiceWithQuote = lazyPage(() => import('./pages/quotes/OsCreateInvoiceWithQuote'));
const CreateSalesmanType = lazyPage(() => import('./components/Salesmans/salesman-types/CreateSalesmanType'));
const EditSalesmanType = lazyPage(() => import('./components/Salesmans/salesman-types/SalesmanTypeEdit'));
const UnpaidInvoices = lazyPage(() => import('./pages/invoices/UnpaidInvoices'));
const OsRequisitionCreatePurchases = lazyPage(() => import('./components/Invoice_Requisition/OsRequisitionCreatePurchases'));
const Invoice_Requisition = lazyPage(() => import('./components/Invoice_Requisition/Invoice_Requisitions'));
const ManageCreditNotes = lazyPage(() => import('./pages/credit-notes/ManageCreditNotes'));
const ShowCreditNote = lazyPage(() => import('./pages/credit-notes/ShowCreditNote'));
 
const RequireNonUser = ({ roleLoaded, appUserRole, children }) => {
  const params = useParams();
  if (!roleLoaded) return <MwSpinner />;
  if (appUserRole === "user") {
    return <Navigate to={`/${params.tenant}/dashboard`} replace />;
  }
  return children;
};

function App() {
  const [openMenu,setOpenMenu] = useState(false);
  const langState = useSelector((state) => state.lang);
  const authUser = useSelector((state) => state.auth.user);
  const {tenant,tenantUsername,loading:loadingTenant,companiesList,branchesList,tenantId,companySelected,branchSelected} = useTenant();
  const {startup} = useStartup();

  const [onShowPaymentAlert,setOnshowPaymentAlert] = useState(false)
  const [appUserRole, setAppUserRole] = useState('user');
  const [roleLoaded, setRoleLoaded] = useState(false);

  useEffect(() =>{
    if(tenant){
      setOnshowPaymentAlert(!tenant.isActive )
    }
  },[tenant]);

  // Force document direction based on language
  useEffect(() => {
    document.documentElement.dir = langState?.value === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = langState?.value;
  }, [langState?.value]);

  useEffect(() => {
    let active = true;
    const loadRole = async () => {
      if (!authUser?._id) {
        setRoleLoaded(false);
        return;
      }
      try {
        const appId = await getCurrentAppId();
        if (!appId) {
          if (active) {
            setAppUserRole('user');
            setRoleLoaded(true);
          }
          return;
        }
        const permissions = await fetchUserPermissions(authUser._id, true);
        const appPermission = getAppPermission(appId, permissions);
        if (active) {
          setAppUserRole(appPermission?.userRole || 'user');
          setRoleLoaded(true);
        }
      } catch (e) {
        if (active) {
          setAppUserRole('user');
          setRoleLoaded(true);
        }
      }
    };
    if (!startup || loadingTenant) return;
    loadRole();
    return () => {
      active = false;
    };
  }, [startup, loadingTenant, authUser?._id]);

  return (
     <ThemeProvider>
      <DndProvider backend={HTML5Backend}>
      {
    !loadingTenant && startup ? (
      // The footer is position:fixed (it takes no room), so keep its height free at the bottom:
      // otherwise it covers the bottom of the page area and its two rounded corners.
      <div className="h-screen flex flex-col flex-between" style={{ paddingBottom: 'var(--footer-height, 28px)' }}>
        <HeaderNavBar
          companySelected = {companySelected}
          branchSelected = {branchSelected}
          companiesList = {companiesList}
          branchesList = {branchesList}
          tenantUsername = {tenantUsername}
          tenant = {tenant}
        />
        {/* Frame-colored background: no white slivers show around the page's rounded corners. */}
        <div className="primary-bg-color flex flex-1 min-h-0 m-auto w-full">
          <MenuSide
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
          />
          {/* The closed side menu is fixed and 52px wide: leave exactly that much room (it was 40px, so the
              menu covered the page's two corners on its side). Same 8px gap on every side of the page. */}
          <div className={`flex flex-col flex-1 min-h-0 p-2 primary-bg-color ${openMenu ? 'ms-10 md:ms-0' :'md:ms-[52px]'}`}>
            {/* The white page area. The outer frame owns the rounded corners and clips its content
                (scrollbar included), so all four corners are rounded the same; it fills the space
                between the header and the footer exactly (flex), so the bottom corners are never cut.
                The inner .finance-route-shell is the scrolling element the page styles rely on. */}
            <div className="finance-route-frame flex flex-col flex-1 min-h-0 rounded-[14px] overflow-hidden bg-[#f8fafc] shadow-sm">
            <div className="finance-route-shell flex-1 min-h-0 p-3 pb-10 bg-[#f8fafc] overflow-auto">
              <MwModal setOnShow={setOnshowPaymentAlert} onShow={onShowPaymentAlert} title="Payment Alert">
                <NoPayment/>
              </MwModal>
              <Suspense fallback={<div className="relative min-h-[60vh]"><MwLoadingOverlay /></div>}>
              <Routes>
                <Route path=":tenant/"   element={ <DashBoard/>} />
                <Route path=":tenant/dashboard"   element={ <DashBoard/>} />
                <Route path=":tenant/token/:token" element={<TokenRedirect/>} />
                <Route path=":tenant/invoices"   element={<ManageInvoices/> } />
                <Route path=":tenant/invoices/show/:id/:companyId/:branchId"   element={!loadingTenant && <ShowInvoice/> } />
                <Route path=":tenant/invoices/print/:id/:companyId/:branchId"   element={!loadingTenant && <ShowInvoice isPrint={true} /> } />
                <Route path=":tenant/invoices/delivery/:id/:companyId/:branchId"   element={!loadingTenant && <ShowInvoice isPrint={true} isDelivery={true} /> } />
                <Route path=":tenant/settings"   element={!loadingTenant && <RequireNonUser roleLoaded={roleLoaded} appUserRole={appUserRole}><Settiings /></RequireNonUser> } />
                <Route  
                  path=":tenant/invoices/create"  
                  element={  
                    <CreateInvoice 
                    tenantId = {tenantId}
                    tenantUsername ={tenantUsername}
                    companySelected = {companySelected}
                    branchSelected = {branchSelected}
                    /> } 
                  />
                <Route  
                  path=":tenant/invoices/edit/:id"  
                  element={  <EditInvoice /> } 
                  />
                <Route  
                  path=":tenant/invoices-returns/:id"  
                  element={  <CreateReturnsInvoice /> } 
                  />
                <Route path=":tenant/credit-notes" element={<ManageCreditNotes/>} />
                <Route path=":tenant/credit-notes/:id" element={!loadingTenant && <ShowCreditNote/>} />
                <Route path=":tenant/invoices-returns/show/:id/:companyId/:branchId"   element={!loadingTenant && <ShowInvoiceReturns/> } />
                <Route  
                  path=":tenant/quotes/edit/:id"  
                  element={  <EditQuote /> } 
                  />
                <Route  
                  path=":tenant/quotes/pdf/:id"  
                  element={  <QuotePdf /> } 
                  />
                <Route  
                path=":tenant/quotes/create"  
                element={  
                    <CreateQuote 
                        tenantId = {tenantId}
                        tenantUsername ={tenantUsername}
                        companySelected = {companySelected}
                        branchSelected = {branchSelected}
                    /> } 
                />
                <Route path=":tenant/quotes"   element={<ManageQuotes/> } />
                <Route path=":tenant/quotes/show/:id/:companyId/:branchId"   element={!loadingTenant && <ShowQuote/> } />

                <Route  
                  path=":tenant/weight/view"  
                  element={  
                    <OsViewWeights 
                    tenantId = {tenantId}

                    /> } 
                  />
                <Route  
                  path=":tenant/weight/create"  
                  element={  
                    <OsCreateWeight 
                    // tenantId = {tenantId}
                    
                    /> } 
                  />
                <Route  
                  path=":tenant/weight/create/:id"  
                  element={  
                    <OsEditWeight 
                    // tenantId = {tenantId}
                    /> } 
                  />
                <Route  
                  path=":tenant/weight/details/:id"  
                  element={  
                    <OsUnitDetails  
                    
                    /> } 
                  />
                <Route  
                  path=":tenant/price/alllists/:id"  
                  element={  
                    <OsUnitDetails  
                    
                    /> } 
                  />
                <Route  
                  path=":tenant/price/onelist/:id"  
                  element={  
                    <OsUnitDetails  
                    
                    /> } 
                  />
                <Route  
                  path=":tenant/price/table"  
                  element={  
                    <CustomTable  
                    
                    /> } 
                  />
 
                <Route path=":tenant/products-home" element={<ProductsHome/>} />
                <Route path=":tenant/products" element={<Products/>} />
                <Route path=":tenant/products/add" element={<CreateProduct/>} />
                <Route path=":tenant/customers" element={<Customers/>} />
                <Route path=":tenant/customers/add" element={<CreateCustomer/>} />
                <Route path=":tenant/customers/show/:id/:companyId/:branchId" element={<ShowCustomer/>} />
                <Route path=":tenant/salesmans" element={<RequireNonUser roleLoaded={roleLoaded} appUserRole={appUserRole}><Salesmans/></RequireNonUser>} />
                <Route path=":tenant/salesmans/add" element={<RequireNonUser roleLoaded={roleLoaded} appUserRole={appUserRole}><CreateSalesman/></RequireNonUser>} />
                <Route path=":tenant/salesmans/edit/:id" element={<RequireNonUser roleLoaded={roleLoaded} appUserRole={appUserRole}><EditSalesman/></RequireNonUser>} />
                <Route path=":tenant/salesmans/types/add" element={<RequireNonUser roleLoaded={roleLoaded} appUserRole={appUserRole}><CreateSalesmanType/></RequireNonUser>} />
                <Route path=":tenant/salesmans/types/edit/:id" element={<RequireNonUser roleLoaded={roleLoaded} appUserRole={appUserRole}><EditSalesmanType/></RequireNonUser>} />
                <Route path=":tenant/transactions" element={<Transactions/>} />
                <Route path=":tenant/reports" element={<RequireNonUser roleLoaded={roleLoaded} appUserRole={appUserRole}><Reports/></RequireNonUser>} />
                <Route  path=":tenant/logout"  element={ <Logout/> } />
                <Route  
                  path=":tenant/invoices/create/:id"  
                  element={  <OsCreateInvoiceWithQuote /> } 
                  />
                <Route  
                  path=":tenant/invoices/unpaid"  
                  element={  <RequireNonUser roleLoaded={roleLoaded} appUserRole={appUserRole}><UnpaidInvoices /></RequireNonUser> } 
                  />
                  <Route path=":tenant/invoices-requisition/create/:id" element={<OsRequisitionCreatePurchases/>} />
                  <Route path=":tenant/invoice-requisition" element={<Invoice_Requisition/>} />
                <Route path="/notfound"  errorElement={<NotFound/> }  />
                <Route path="/*"  errorElement={<NotFound/> }  />
              </Routes>
              </Suspense>
            </div>
            </div>
          </div>
        </div>
        <Footer/>
      </div>
    ) : <MwSpinner/>}
    </DndProvider>
    </ThemeProvider>  
  );
}

export default withTenant(withGuard(App));

